import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { StatusBadge } from '@/components/StatusBadge';
import { PageHeader } from '@/components/PageHeader';
import { useTranslation } from 'react-i18next';
import { formatDate } from '@/lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Globe, Plus, RefreshCw, Activity } from 'lucide-react';

interface Domain {
  id: string;
  domain: string;
  status: string;
  // live D1 columns are snake_case
  verification_token?: string;
  verification_records?: Array<{ type?: string; name?: string; value?: string }>;
  last_verified_at?: number;
  createdAt?: number;
  created_at?: number;
  organization_name?: string;
  organization_id?: string;
}

const PAGE_SIZE = 20;

function Domains() {
  const { t } = useTranslation();
  const [domains, setDomains] = useState<Domain[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [dnsStatusDialogOpen, setDnsStatusDialogOpen] = useState(false);
  const [selectedDomainId, setSelectedDomainId] = useState<string | null>(null);
  const [dnsStatus, setDnsStatus] = useState<any>(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [newDomain, setNewDomain] = useState({
    domain: '',
  });

  useEffect(() => {
    checkUserRole();
  }, []);

  useEffect(() => {
    fetchDomains();
  }, [page, appliedSearch]);

  const checkUserRole = async () => {
    try {
      const data = await api.get(`/api/users/me`);
        setIsSuperAdmin(data.data?.role === 'SUPER_ADMIN');
    } catch (error) {
      console.error('Failed to check user role:', error);
    }
  };

  const fetchDomains = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(PAGE_SIZE) });
      if (appliedSearch) params.set('search', appliedSearch);
      const data = await api.get(`/api/domains?${params.toString()}`);
      setDomains(data.data || []);
      setTotal(data.pagination?.total ?? (data.data || []).length);
    } catch (error) {
      console.error('Failed to fetch domains:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify = async (id: string) => {
    try {
      await api.post(`/api/domains/${id}/verify`);
      fetchDomains();
    } catch (error: any) {
      console.error('Failed to verify domain:', error);
      alert(`${t('common.error')}: ${error?.message || 'verification failed'}`);
    }
  };

  const handleCreateDomain = async () => {
    try {
      await api.post('/api/domains', { domain: newDomain.domain });
      setIsCreateDialogOpen(false);
      setNewDomain({ domain: '' });
      fetchDomains();
    } catch (error: any) {
      console.error('Failed to create domain:', error);
      alert(`${t('common.error')}: ${error?.message || 'failed to add domain'}`);
    }
  };

  const handleDeleteDomain = async () => {
    if (!selectedDomainId) return;

    try {
      await api.del(`/api/domains/${selectedDomainId}`);
      setDeleteDialogOpen(false);
      setSelectedDomainId(null);
      fetchDomains();
    } catch (error: any) {
      console.error('Failed to delete domain:', error);
      alert(`${t('common.error')}: ${error?.message || 'failed to delete'}`);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const openDeleteDialog = (id: string) => {
    setSelectedDomainId(id);
    setDeleteDialogOpen(true);
  };

  const handleCheckDNS = async (id: string) => {
    try {
      const data = await api.get(`/api/domains/${id}/dns-status`);
        setDnsStatus(data.data);
        setSelectedDomainId(id);
        setDnsStatusDialogOpen(true);
    } catch (error) {
      console.error('Failed to check DNS status:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="w-full px-4 py-6 sm:px-6 lg:px-8">
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">{t('common.loading')}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="w-full space-y-6 px-4 py-6 sm:px-6 lg:px-8">
      <PageHeader
        eyebrow="Configuration"
        icon={Globe}
        title={t('domains.title', { defaultValue: 'Domains' })}
        subtitle={t('domains.subtitle', { defaultValue: 'Prove ownership of the email domains your organization signs in with' })}
        actions={(
          <Button onClick={() => setIsCreateDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            {t('domains.addDomain', { defaultValue: 'Add domain' })}
          </Button>
        )}
      />

      <form
        className="flex items-center gap-2"
        onSubmit={(e) => { e.preventDefault(); setPage(1); setAppliedSearch(search.trim()); }}
      >
        <Input
          placeholder={t('common.search')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-xs"
        />
        <Button type="submit" variant="outline">{t('common.search')}</Button>
        {appliedSearch && (
          <Button
            type="button"
            variant="ghost"
            onClick={() => { setSearch(''); setAppliedSearch(''); setPage(1); }}
          >
            {t('common.clear')}
          </Button>
        )}
      </form>

      <Card>
        <CardHeader>
          <CardTitle>{t('navigation.domains')}</CardTitle>
          <CardDescription>
            {t('domains.configured', { count: total, defaultValue: '{{count}} domains configured' })}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('domains.table.domain', { defaultValue: 'Domain' })}</TableHead>
                {isSuperAdmin && <TableHead>{t('organizations.title', 'Organization')}</TableHead>}
                <TableHead>{t('licenses.status')}</TableHead>
                <TableHead>{t('domains.table.verificationToken', { defaultValue: 'Verification record' })}</TableHead>
                <TableHead>{t('licenses.createdAt')}</TableHead>
                <TableHead>{t('domains.table.verified', { defaultValue: 'Verified' })}</TableHead>
                <TableHead className="text-right">{t('users.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {domains.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isSuperAdmin ? 7 : 6} className="text-center text-muted-foreground">
                    {t('dashboard.noData')}
                  </TableCell>
                </TableRow>
              ) : (
                domains.map((domain) => (
                  <TableRow key={domain.id}>
                    <TableCell className="font-medium">{domain.domain}</TableCell>
                    {isSuperAdmin && (
                      <TableCell>
                        <span className="text-sm text-muted-foreground">
                          {domain.organization_name || '-'}
                        </span>
                      </TableCell>
                    )}
                    <TableCell>
                      <StatusBadge status={domain.status} />
                    </TableCell>
                    <TableCell className="font-mono text-xs max-w-xs break-words">
                      {domain.verification_token || domain.verification_records?.[0]?.value || '-'}
                    </TableCell>
                    <TableCell>
                      {formatDate(domain.created_at || domain.createdAt)}
                    </TableCell>
                    <TableCell>
                      {domain.last_verified_at
                        ? formatDate(domain.last_verified_at)
                        : '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        {!domain.last_verified_at && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleVerify(domain.id)}
                          >
                            <RefreshCw className="h-4 w-4 mr-1" />
                            {t('domains.verify', { defaultValue: 'Verify' })}
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleCheckDNS(domain.id)}
                        >
                          <Activity className="h-4 w-4 mr-1" />
                          {t('domains.dns', { defaultValue: 'Check DNS' })}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openDeleteDialog(domain.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          {t('domains.delete', { defaultValue: 'Delete' })}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-4">
              <p className="text-sm text-muted-foreground">
                {t('common.page', 'Page')} {page} / {totalPages}
              </p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                  {t('common.previous')}
                </Button>
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>
                  {t('common.next')}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Domain Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('domains.dialogs.addDomain.title', { defaultValue: 'Add domain' })}</DialogTitle>
            <DialogDescription>
              {t('domains.dialogs.addDomain.description', { defaultValue: 'Add the domain, then publish the TXT record shown in the table at your DNS provider.' })}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="domain">{t('domains.table.domain', { defaultValue: 'Domain' })}</Label>
              <Input
                id="domain"
                value={newDomain.domain}
                onChange={(e) => setNewDomain({ domain: e.target.value })}
                placeholder={t('domains.dialogs.addDomain.placeholder', { defaultValue: 'example.com' })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleCreateDomain} disabled={!newDomain.domain}>
              {t('domains.addDomain', { defaultValue: 'Add domain' })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('domains.dialogs.deleteDomain.title', { defaultValue: 'Delete domain?' })}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('domains.dialogs.deleteDomain.description', { defaultValue: 'Users with this email domain will no longer be matched to your organization. This cannot be undone.' })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteDomain}>
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* DNS Status Dialog */}
      <Dialog open={dnsStatusDialogOpen} onOpenChange={setDnsStatusDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('domains.dialogs.dnsStatus.title', { defaultValue: 'DNS status' })}</DialogTitle>
            <DialogDescription>
              {t('domains.dialogs.dnsStatus.description', { defaultValue: 'Result of the latest lookup for the verification TXT record.' })}
            </DialogDescription>
          </DialogHeader>
          {dnsStatus && (
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label>{t('domains.dialogs.dnsStatus.status', { defaultValue: 'Status' })}</Label>
                <StatusBadge status={dnsStatus.verified ? 'VERIFIED' : 'PENDING'} />
              </div>
              {dnsStatus.records && (
                <div className="grid gap-2">
                  <Label>{t('domains.dialogs.dnsStatus.dnsRecordsFound', { defaultValue: 'DNS records found' })}</Label>
                  <div className="text-sm font-mono bg-muted p-2 rounded">
                    {Array.isArray(dnsStatus.records) ? (
                      dnsStatus.records.map((record: any, idx: number) => (
                        <div key={idx}>{JSON.stringify(record)}</div>
                      ))
                    ) : (
                      <div>{JSON.stringify(dnsStatus.records)}</div>
                    )}
                  </div>
                </div>
              )}
              {dnsStatus.message && (
                <div className="grid gap-2">
                  <Label>{t('domains.dialogs.dnsStatus.message', { defaultValue: 'Message' })}</Label>
                  <p className="text-sm">{dnsStatus.message}</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setDnsStatusDialogOpen(false)}>
              {t('common.close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default Domains;
